<template>
  <div class="print-pay-fee-container">
    <!-- 标题区：图标+小区名整体居中 -->
    <div class="header-area">
      <h1 class="title-text"><span class="logo-icon">◎</span>{{printPayFeeInfo.communityName}}收款单据</h1>
    </div>

    <!-- NO编号在标题下方，靠右 -->
    <div class="receipt-info-bar">
      <div class="receipt-no">NO. <span class="num">{{printPayFeeInfo.receiptNum}}</span></div>
    </div>

    <!-- 基本信息 + 结算方式同一行 -->
    <div class="info-bar">
      <span>日期：<i class="num">{{dateFormatShort(printPayFeeInfo.feeTime)}}</i></span>
      <span class="customer-name-inline">客户名称：<i>{{_formatPayObjName(printPayFeeInfo.payObjName, printPayFeeInfo.roomName)}}</i></span>
      <span class="pay-method-right">结算方式：<i>{{_getPayMethod()}}</i></span>
    </div>

    <!-- 明细表格 -->
    <table class="receipt-table">
      <thead>
        <tr>
          <th class="col-item">开具项目</th>
          <th class="col-qty">数量/面积</th>
          <th class="col-price">单价</th>
          <th class="col-money">金额</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in printPayFeeInfo.fees" :key="index">
          <td class="col-item td-left">
            <span class="payer-label" v-if="_isVehicleFee(item) && _getObjName(item)">{{_getObjName(item)}}</span>{{item.feeName}}
            <template v-if="item.feeTypeCd !== '888800010006'">
              {{_getFeePeriod(item)}}
            </template>
            <template v-if="item.preDegrees"><br/>读数:{{item.preDegrees}}~{{item.curDegrees}}</template>
          </td>
          <td class="col-qty td-center"><span class="num">{{item.area || ''}}</span></td>
          <td class="col-price td-center"><span class="num">{{item.squarePrice}}</span></td>
          <td class="col-money td-right"><span class="num">{{parseFloat(item.amount).toFixed(2)}}</span></td>
        </tr>
        <tr class="row-total">
          <td colspan="3" class="td-left total-label">合计（大写金额）：<b>{{changeNumMoneyToChinese(printPayFeeInfo.amount)}}</b></td>
          <td class="td-right total-num"><b>¥:<span class="num">{{printPayFeeInfo.amount}}</span></b></td>
        </tr>
        <tr class="row-remark">
          <td colspan="4" class="td-left remark-content">
            备注：{{ printPayFeeInfo.customRemark || '' }}
            <template v-if="printPayFeeInfo.content && !printPayFeeInfo.customRemark"><span v-html="printPayFeeInfo.content"></span></template>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 底部区域（表格外） -->
    <div class="footer-outside">
      <span class="footer-note-left">凭此收款收据十日内到服务中心换取电子发票</span>
      <span class="footer-payee-right">收款人：{{userInfo.name}}</span>
    </div>

    <!-- 手动录入备注（打印时隐藏） -->
    <div id="print-btn" class="action-buttons">
      <el-input v-model="printPayFeeInfo.customRemark" type="textarea" :rows="2" 
        placeholder="手动录入备注（选填，将显示在收据备注栏）" style="width:400px;margin-bottom:8px;" />
      <div>
        <el-button type="primary" size="small" @click="handlePrint">
          <i class="el-icon-printer"></i>&nbsp;打印
        </el-button>
        <el-button type="warning" size="small" style="margin-right:10px;" @click="handleClose">
          取消
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getCommunityId, getCommunityName } from '@/api/community/communityApi'
import { queryFeeReceipt, queryFeeReceiptDetail, queryFeePrintSpec, queryOwnerCars } from '@/api/fee/printPayFeeApi'
import { querySettingValue } from '@/api/system/communitySettingManageApi'
import { getUserId, getUserName } from '@/api/user/userApi'
import { dateFormat } from '@/utils/dateUtil'
import { changeNumMoneyToChinese } from '@/utils/moneyUtil'
import { queryOwners } from '@/api/owner/ownerApi' 

export default {
  name: 'PrintPayFeeList',
  data() {
    return {
      printPayFeeInfo: {
        communityName: '',
        receiptId: '',
        receiptIds: '',
        detailIds: '',
        roomName: '',
        amount: 0.00,
        fees: [],
        feeTime: '',
        wechatName: '',
        content: '',
        qrImg: '',
        payObjName: '',
        objType: '',
        feeReceipt: [],
        apply: 'N',
        receiptNum: '',
        acctAmount: 0,
        customRemark: ''
      },
      userInfo: {},
      printFlag: '0',
      communityId: '',
      shortRoom: false
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.userInfo = {
      userId: getUserId(),
      name: getUserName()
    }
    this.initData()
  },
  methods: {
    async initData() {
      this.printPayFeeInfo.receiptId = this.$route.query.receiptId
      this.printPayFeeInfo.receiptIds = this.$route.query.receiptIds
      this.printPayFeeInfo.detailIds = this.$route.query.detailIds
      if(this.$route.query.apply){
        this.printPayFeeInfo.apply = this.$route.query.apply
      }
      this.printPayFeeInfo.merge = this.$route.query.merge
      console.log('===== 打印页 initData: merge=' + this.printPayFeeInfo.merge + ', receiptIds=' + this.printPayFeeInfo.receiptIds + ' =====')

      // 加载小区名称前缀配置
      const prefix = await this._loadNamePrefix()
      this.printPayFeeInfo.communityName = prefix + getCommunityName()

      this.loadReceipt()
      this.loadPrintSpec()
      this._loadShortRoomSetting()
    },
    async _loadShortRoomSetting() {
      try {
        const val = await querySettingValue({
          communityId: this.communityId,
          settingKey: 'RECEIPT_ROOM_SHORT',
          settingType: '2002'
        })
        this.shortRoom = (val === '1')
      } catch (e) { /* ignore */ }
    },
    async _loadNamePrefix() {
      try {
        const prefix = await querySettingValue({
          communityId: this.communityId,
          settingKey: 'RECEIPT_NAME_PREFIX',
          settingType: '2002'
        })
        return prefix || ''
      } catch (e) {
        console.error('获取名称前缀失败', e)
        return ''
      }
    },
    loadReceipt() {
      const params = {
        page: 1,
        row: 30,
        receiptId: this.printPayFeeInfo.receiptId,
        receiptIds: this.printPayFeeInfo.receiptIds,
        detailIds: this.printPayFeeInfo.detailIds,
        communityId: this.communityId
      }

      queryFeeReceipt(params).then(response => {
        const feeReceipt = response.data
        // receivableAmount 是收据应收总额，INNER JOIN 时每行相同，直接取第一行即可
        const amount = parseFloat((feeReceipt[0] && feeReceipt[0].receivableAmount) || 0)
        this.printPayFeeInfo.amount = amount.toFixed(2)
        // 优先用后端 freshRoomInfo 算好的 roomName：车辆收据时=业主房屋编号，房屋收据时=房号；兜底 objName
        // 业主有多套房屋时后端会用 "/" 拼接，这里只取一个（业主名下只显示一个房号）
        // 合并打印时优先从房屋类收据获取房号，避免首条是停车费(objType=6666)时丢失
        let _roomName = ''
        for (const receipt of feeReceipt) {
          if (receipt.objType !== '6666') {
            _roomName = receipt.roomName || receipt.objName || ''
            break
          }
        }
        if (!_roomName) {
          _roomName = feeReceipt[0].roomName || feeReceipt[0].objName || ''
        }
        if (_roomName && _roomName.indexOf('/') > -1) {
          _roomName = _roomName.split('/')[0].trim()
        }
        this.printPayFeeInfo.roomName = _roomName
        this.printPayFeeInfo.objType = feeReceipt[0].objType
        this.printPayFeeInfo.feeTime = feeReceipt[0].createTime
        this.printPayFeeInfo.payObjName = feeReceipt[0].payObjName
        this.printPayFeeInfo.feeReceipt = feeReceipt
        this.printPayFeeInfo.receiptNum = feeReceipt[0].receiptCode

        // 补全客户名称：预收票据/无业主收据的 payObjName 存的是房号，
        // 用房间ID查业主，补成"业主姓名"，使打印显示"业主姓名（房号）"
        this._fillOwnerName(feeReceipt[0])

        this.loadReceiptDetail()
      })
    },
    // 用房间ID查询业主名，补全 payObjName（仅当 payObjName 是房号、非真实业主名时）
    _fillOwnerName(firstReceipt) {
      // 仅房屋类费用(objType=3333)且有 objId 时处理
      if (!firstReceipt || firstReceipt.objType !== '3333' || !firstReceipt.objId) {
        return
      }
      const payObjName = firstReceipt.payObjName || ''
      const roomName = firstReceipt.roomName || firstReceipt.objName || ''
      // payObjName 与房号相同（无业主，房号被当成了客户名），才需要补全
      if (payObjName && payObjName !== roomName && payObjName !== firstReceipt.objName) {
        return
      }
      queryOwners({
        page: 1,
        row: 1,
        roomId: firstReceipt.objId
      }).then(res => {
        const owners = res.owners || res.data || []
        if (owners.length > 0 && owners[0].name && owners[0].ownerId !== '-1') {
          this.$set(this.printPayFeeInfo, 'payObjName', owners[0].name)
        }
      }).catch(() => {
        // 查询失败不影响打印，保留原 payObjName
      })
    },
    loadReceiptDetail() {
      const params = {
        page: 1,
        row: 500,
        receiptId: this.printPayFeeInfo.receiptId,
        receiptIds: this.printPayFeeInfo.receiptIds,
        detailIds: this.printPayFeeInfo.detailIds,
        communityId: this.communityId
        // 不传 mergeFee 给后端：后端聚合(MIN/MAX)会丢失跨月合并的真实起始月份
        // 由前端 _mergeFeeDetails 用原始明细自行合并，保证周期正确
      }
      console.log('===== loadReceiptDetail 请求参数:', JSON.stringify(params) + ' =====')

      queryFeeReceiptDetail(params).then(response => {
        let feeReceiptDetails = response.data
        console.log('===== loadReceiptDetail 返回数据条数=' + (feeReceiptDetails ? feeReceiptDetails.length : 0) + ' =====')
        // 打印前3条的概览
        if (feeReceiptDetails && feeReceiptDetails.length > 0) {
          console.log('===== 返回数据示例: feeName=' + feeReceiptDetails[0].feeName + ', startTime=' + feeReceiptDetails[0].startTime + ', endTime=' + feeReceiptDetails[0].endTime + ', squarePrice=' + feeReceiptDetails[0].squarePrice + ', payerObjId=' + feeReceiptDetails[0].payerObjId + ' =====')
        }
        // 只显示编号，不拼接 payOrderId
        if (feeReceiptDetails[0].amount < 0) {
          this.printPayFeeInfo.apply = 'R'
        }
        let acctAmount = 0.0
        // 从收据级数据取 objName/objType 兜底，再写入每个明细
        const firstReceipt = this.printPayFeeInfo.feeReceipt[0] || {}
        const rootObjName = firstReceipt.objName || ''
        const rootObjType = firstReceipt.objType || ''
        feeReceiptDetails.forEach(item => {
          acctAmount = acctAmount + parseFloat(item.acctAmount)
          this.printPayFeeInfo.feeReceipt.forEach(im => {
            if (item.receiptId == im.receiptId) {
              item.objName = im.objName
              item.feeTypeCd = im.feeTypeCd
              item.objType = im.objType
            }
          })
          // 兜底：确保所有明细都有 objName/objType，避免合并 key 不一致
          if (!item.objName || item.objName === '-') {
            item.objName = rootObjName
          }
          if (!item.objType) {
            item.objType = rootObjType
          }
        })

        // 单张收据始终合并同名费用，合并打印(merge=ON)时跨收据合并
        // receiptIds 非空 → 多收据合并打印，仅 merge=ON 时合并
        // receiptIds 为空 → 单张收据，始终合并
        const isMultiReceipt = this.printPayFeeInfo.receiptIds && this.printPayFeeInfo.receiptIds.length > 0
        if (this.printPayFeeInfo.merge === 'ON' || !isMultiReceipt) {
          feeReceiptDetails = this._mergeFeeDetails(feeReceiptDetails)
        } else {
          // 未合并的多收据场景：单条明细若为动态费用(squarePrice=0/空)，用 amount 兜底当单价
          feeReceiptDetails.forEach(item => {
            if ((!item.squarePrice || parseFloat(item.squarePrice) === 0) && parseFloat(item.amount || 0) > 0) {
              item.squarePrice = parseFloat(item.amount).toFixed(2)
            }
          })
        }

        // 排序：先房屋类费用，再车辆类费用；物业费排在房屋类最前；车辆类按车牌排序
        if (feeReceiptDetails && feeReceiptDetails.length > 1) {
          feeReceiptDetails.sort((a, b) => {
            const aIsCar = this._isVehicleFee(a)
            const bIsCar = this._isVehicleFee(b)
            // 房屋类在前，车辆类在后
            if (!aIsCar && bIsCar) return -1
            if (aIsCar && !bIsCar) return 1
            // 同类别内：物业费排在前面
            const aIsWy = (a.feeName || '').indexOf('物业') !== -1
            const bIsWy = (b.feeName || '').indexOf('物业') !== -1
            if (aIsWy && !bIsWy) return -1
            if (!aIsWy && bIsWy) return 1
            // 车辆类内按车牌号排序
            if (aIsCar && bIsCar) {
              const aPlate = this._extractCarPlate(a) || a.objName || ''
              const bPlate = this._extractCarPlate(b) || b.objName || ''
              return aPlate.localeCompare(bPlate, 'zh-CN')
            }
            return 0
          })
        }

        // 用合并后的明细重算总额（与行明细同源，保证一致性）
        let mergedTotal = 0.0
        feeReceiptDetails.forEach(item => {
          mergedTotal += parseFloat(item.amount || 0)
        })
        this.printPayFeeInfo.amount = mergedTotal.toFixed(2)

        this.printPayFeeInfo.fees = feeReceiptDetails
        this.printPayFeeInfo.acctAmount = acctAmount.toFixed(2)

        // 补全车辆费用车牌号：预收票据/部分收据的明细 objName 是房号而非车牌，
        // 用 payerObjId(车辆时=carId) 批量查询车牌，写回 objName 供 _getObjName 显示
        this._fillCarNums(feeReceiptDetails)
      })
    },
    // 批量补全车辆费用明细的车牌号
    _fillCarNums(details) {
      if (!details || details.length === 0) return
      // 收集需要补全车牌的 carId（车辆类费用且 objName 不是车牌格式）
      const carIdSet = new Set()
      details.forEach(item => {
        if (!this._isVehicleFee(item)) return
        const name = item.objName || ''
        // 车牌正则：汉字+字母+5-6位字母数字
        if (/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/.test(name)) return
        const carId = item.payerObjId || ''
        if (carId && carId !== '-') carIdSet.add(carId)
      })
      if (carIdSet.size === 0) return
      const carIds = Array.from(carIdSet)
      // 逐个查询车辆（车辆接口支持 carId 单查）
      carIds.forEach(carId => {
        queryOwnerCars({ page: 1, row: 1, carId }).then(res => {
          // queryOwnerCars 返回结构：车辆数组在 res.data.data（与 CarTreeDiv 等组件一致）
          const cars = (res.data && res.data.data) || res.ownerCars || res.data || []
          if (cars.length > 0 && cars[0].carNum) {
            const carNum = cars[0].carNum
            // 写回所有匹配该 carId 的明细（用 $set 保证 Vue2 响应式更新）
            this.printPayFeeInfo.fees.forEach((item, idx) => {
              if (this._isVehicleFee(item) && (item.payerObjId === carId)) {
                this.$set(this.printPayFeeInfo.fees[idx], 'carNum', carNum)
                // 若 objName 不是车牌格式，用车牌覆盖，避免显示房号
                if (!/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/.test(item.objName || '')) {
                  this.$set(this.printPayFeeInfo.fees[idx], 'objName', carNum)
                }
              }
            })
          }
        }).catch(() => {})
      })
    },
    loadPrintSpec() {
      const params = {
        page: 1,
        row: 1,
        specCd: 2020,
        communityId: this.communityId
      }

      queryFeePrintSpec(params).then(response => {
        const data = response.data
        if (data.length > 0) {
          this.printPayFeeInfo.content = data[0].content
          this.printPayFeeInfo.qrImg = data[0].qrImg
          if (data[0].printName) {
            this.printPayFeeInfo.communityName = data[0].printName
          }
        }
      })
    },
    dateFormat(date) {
      if (!date) return ''
      return dateFormat(date)
    },
    dateFormatShort(date) {
      if (!date) return ''
      const d = new Date(date)
      if (isNaN(d.getTime())) return date
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return y + '-' + m + '-' + dd
    },
    changeNumMoneyToChinese,
    _getPayMethod() {
      if (this.printPayFeeInfo.fees && this.printPayFeeInfo.fees.length > 0) {
        const method = this.printPayFeeInfo.fees[0].primeRate
        if (method) {
          // 账户抵扣支付方式显示处理
          if (method === '100') return '账户抵扣'
          return method
        }
      }
      return ''
    },
    // 是否纯车辆收据且车辆未绑定房屋：全部是车辆(objType=6666)且 roomName 为 "-"（后端未查到有效房屋绑定）
    _isOnlyUnboundVehicle() {
      const receipts = this.printPayFeeInfo.feeReceipt
      if (!receipts || receipts.length === 0) return false
      return receipts.every(r => r.objType === '6666' && (!r.roomName || r.roomName === '-'))
    },
    // 格式化客户名称显示：
    // - payObjName 与 roomName 相同（无业主，generator 用房号当 name）：只显示一次，不重复括号
    // - payObjName 是业主名（与 roomName 不同）：显示"业主名（房号）"
    // - 纯未绑定车辆：只显示 payObjName
    _formatPayObjName(payObjName, roomName) {
      if (!payObjName) return ''
      const room = this._formatRoomName(roomName)
      // 无房号 或 纯未绑定车辆：只显示客户名称
      if (!room || this._isOnlyUnboundVehicle()) {
        return payObjName
      }
      // payObjName 与房号相同（或规范化后相同）：说明无业主，房号被当成了客户名，只显示一次
      if (payObjName === room || payObjName === roomName) {
        return payObjName
      }
      // 正常情况：业主名（房号）
      return payObjName + '（' + room + '）'
    },
    // 判断是否为车辆类费用（以费用名称为准，避免公摊/物业类费用被误标为车辆）
    _isVehicleFee(item) {
      if (!item) return false
      const feeName = (item.feeName || '')
      // 费用名称明确是车辆/停车/车位相关才认为是车辆类
      if (/车辆|停车|车位/.test(feeName)) return true
      // 公摊、物业类费用即使 objType=6666 也按房屋类显示
      if (feeName.indexOf('公摊') !== -1 || feeName.indexOf('物业') !== -1) return false
      // 兜底：objType=6666 且费用类型为停车费 且对象名含车牌
      if (item.objType === '6666' && item.feeTypeCd === '888800010008') {
        const name = item.objName || item.carNum || ''
        if (/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/.test(name)) return true
      }
      return false
    },
    // 格式化房号：24栋1单元0102室 → 24-1-0102；开启短格式 → 24-102
    _formatRoomName(name) {
      if (!name) return ''
      const norm = name.replace(/栋/, '-').replace(/单元/, '-').replace(/室$/, '')
      if (!this.shortRoom) return norm
      // 短格式: 1-1-1502 → 1-1502, 3-1-0502 → 3-502
      const parts = norm.split('-')
      if (parts.length >= 3) {
        const bld = parts[0]
        const room = parts[2].replace(/^0+/, '')
        return bld + '-' + room
      }
      return norm
    },
    _getObjName(item) {
      let name = item.objName || ''
      if (!name) {
        // objName 为空时回退到 carNum 字段
        name = item.carNum || ''
      }
      if (!name) return ''
      const m = name.match(/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/)
      if (m) return m[0]
      // 车牌号未匹配到时也尝试 carNum 提取
      if (item.carNum && item.carNum !== '-' && item.carNum !== name) {
        const m2 = item.carNum.match(/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/)
        if (m2) return m2[0]
      }
      return name
    },
    // 提取车牌号用于合并分组键（不包含 - 车位等后缀）
    _extractCarPlate(item) {
      let name = item.objName || ''
      if (!name) name = item.carNum || ''
      if (!name) return ''
      const m = name.match(/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/)
      if (m) return m[0]
      if (item.carNum && item.carNum !== '-' && item.carNum !== name) {
        const m2 = item.carNum.match(/[一-龥][A-Z][A-HJ-NP-Z0-9]{4,6}/)
        if (m2) return m2[0]
      }
      return name
    },
    _getFeePeriod(item) {
      if (!item.startTime || !item.endTime) return ''
      const startStr = this.dateFormatShort(item.startTime)
      const endStr = this.dateFormatShort(item.endTime)
      // 水电表读数类费用（有上期/本期度数），使用实际抄表日期，不做月初/月末归一化
      const hasReading = item.preDegrees || item.curDegrees
      if (hasReading) {
        return startStr + '至' + endStr
      }
      let normStart = this._toMonthStart(startStr)
      let normEnd = this._toMonthEnd(endStr)
      // 归一化后若开始>结束（异常/跨月边界数据），退回实际开始日期，不再强制改 1 号
      if (normStart > normEnd) {
        normStart = this.dateFormatShort(startStr)
      }
      return normStart + '至' + normEnd
    },
    // 开始时间归一：系统里费用周期用 start_time = 上次结束时间 表示，
    // 因此"当月最后一天"要显示为次月 1 号（表示新周期从次月开始）。
    // 其余情况一律保留实际日期——不能强制改成当月 1 号，
    // 否则会把非整月缴费的起点（如 2026-01-15）篡改成 2026-01-01，打印日期与实际不符。
    _toMonthStart(dateStr) {
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
      if (d.getDate() === lastDay) {
        return this.dateFormatShort(new Date(d.getFullYear(), d.getMonth() + 1, 1))
      }
      return this.dateFormatShort(d)
    },
    // 结束时间：直接使用实际日期，不再强制成"当月最后一天"，
    // 否则实际结束日（如 2026-03-20）会被篡改成 2026-03-31，打印日期与实际缴费期间不一致。
    _toMonthEnd(dateStr) {
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return this.dateFormatShort(d)
    },
    // 前端合并：按付费对象+费用名称分组，相同项合并金额和周期
    _mergeFeeDetails(details) {
      if (!details || details.length === 0) return details
      const grouped = {}
      console.log('===== _mergeFeeDetails 输入条数=' + details.length + ' =====')
      details.forEach(item => {
        // 分组键：车辆类费用按车辆ID(payerObjId=carId)分组；房屋/公摊等其他费用按付费对象+费用名称合并
        const isVehicle = this._isVehicleFee(item)
        let rawPayerId
        if (isVehicle) {
          // 关键：车辆费用挂在车辆下(pay_fee.payer_obj_id=carId)，必须用 carId 分组，
          // 不能用车牌/objName——因为收据明细的 objName 可能是房屋号(收据挂在房屋下)，
          // 会导致同一房屋下多辆车被错误合并成一组
          rawPayerId = item.payerObjId || this._extractCarPlate(item) || item.objId || '0'
        } else {
          // 同一客户下同一名称的房屋/公摊费用合并为一条（如多个小区公用公摊）
          rawPayerId = item.payerObjId || item.objId || '0'
        }
        const feeKey = item.feeName || '其他费用'
        const key = `${rawPayerId}-${isVehicle ? 'VEHICLE' : 'HOUSE'}||${feeKey}`
        console.log('===== 合并分组 key=' + key + ', feeId=' + item.feeId + ', payerObjId=' + rawPayerId + ', objName=' + (item.objName || '') + ', objType=' + (item.objType || '') + ', isVehicle=' + isVehicle + ', startTime=' + item.startTime + ', amount=' + item.amount + ' =====')
        // 水电表读数类费用使用实际日期，不做月初/月末归一化
        const hasReading = item.preDegrees || item.curDegrees
        // 每条账单先归一化（非读数类），避免 HC 月账单用月底做边界导致合并后区间错乱
        let normStart = item.startTime ? (hasReading ? this.dateFormatShort(item.startTime) : this._toMonthStart(this.dateFormatShort(item.startTime))) : ''
        let normEnd = item.endTime ? (hasReading ? this.dateFormatShort(item.endTime) : this._toMonthEnd(this.dateFormatShort(item.endTime))) : ''
        // 归一化后开始>结束（异常/跨月边界数据），退回实际开始日期，不再强制改 1 号
        if (normStart && normEnd && !hasReading && normStart > normEnd) {
          normStart = this.dateFormatShort(item.startTime)
        }
        if (!grouped[key]) {
          grouped[key] = {
            ...item,
            startTime: normStart,
            endTime: normEnd,
            amount: parseFloat(item.amount || 0),
            // 仅物业费显示面积
            area: (item.feeTypeCd === '888800010001' || (item.feeName || '').indexOf('物业') !== -1) ? (item.area || '') : '',
            // 记录每月明细金额，供动态费用(squarePrice=0)后续推导单价
            _monthlyAmounts: [parseFloat(item.amount || 0)]
          }
        } else {
          const g = grouped[key]
          g.amount += parseFloat(item.amount || 0)
          g._monthlyAmounts.push(parseFloat(item.amount || 0))
          // 取最早开始时间和最晚结束时间
          if (normStart && (!g.startTime || normStart < g.startTime)) {
            g.startTime = normStart
          }
          if (normEnd && (!g.endTime || normEnd > g.endTime)) {
            g.endTime = normEnd
          }
          // 水表读数合并：取最小上期度数和最大本期度数，数量累加
          if (item.preDegrees != null && g.preDegrees != null) {
            g.preDegrees = Math.min(parseFloat(g.preDegrees), parseFloat(item.preDegrees))
          } else if (item.preDegrees != null) {
            g.preDegrees = item.preDegrees
          }
          if (item.curDegrees != null && g.curDegrees != null) {
            g.curDegrees = Math.max(parseFloat(g.curDegrees), parseFloat(item.curDegrees))
          } else if (item.curDegrees != null) {
            g.curDegrees = item.curDegrees
          }
          // 仅物业费显示面积（不累加），其他费用不显示面积
          const isPropertyFee = item.feeTypeCd === '888800010001' || (item.feeName && item.feeName.indexOf('物业') !== -1)
          if (!isPropertyFee) {
            g.area = ''
          } else if (!g.area) {
            g.area = item.area
          }
          // squarePrice 相同则保留，不同则用 ranged 格式显示范围
          if (item.squarePrice && g.squarePrice && g.squarePrice !== item.squarePrice) {
            g.squarePrice = `${g.squarePrice}~${item.squarePrice}`
          }
        }
      })
      const result = Object.values(grouped)
      // 动态费用(squarePrice=0/空)单价推导：用每月金额按规则推导单价
      result.forEach(g => {
        // 有正常单价的不处理
        if (g.squarePrice && parseFloat(g.squarePrice) > 0) {
          delete g._monthlyAmounts
          return
        }
        const amounts = g._monthlyAmounts || []
        if (amounts.length === 0) {
          delete g._monthlyAmounts
          return
        }
        // 规则：多条取出现次数最多的金额(众数)；并列时取后者(数组靠后)；
        //       全相同取该值；仅1条取该值；不同以后面为主
        // 先统计每个金额出现次数，保留"最后出现"的位置用于并列时取后者
        const countMap = {}
        let maxCount = 0
        let modeAmount = amounts[amounts.length - 1] // 默认取最后一条
        amounts.forEach(amt => {
          const k = amt.toFixed(2)
          countMap[k] = (countMap[k] || 0) + 1
        })
        maxCount = Math.max.apply(null, Object.keys(countMap).map(k => countMap[k]))
        // 找出现次数最多、且在数组中最后出现的金额（并列取后者）
        for (let i = amounts.length - 1; i >= 0; i--) {
          const k = amounts[i].toFixed(2)
          if (countMap[k] === maxCount) {
            modeAmount = amounts[i]
            break
          }
        }
        g.squarePrice = modeAmount.toFixed(2)
        delete g._monthlyAmounts
      })
      console.log('===== _mergeFeeDetails 合并后条数=' + result.length + ' =====')
      result.forEach((r, i) => {
        console.log('===== 合并结果[' + i + ']: feeName=' + r.feeName + ', startTime=' + r.startTime + ', endTime=' + r.endTime + ', amount=' + r.amount + ' =====')
      })
      return result
    },

    handlePrint() {
      this.printFlag = '1'
      document.getElementById("print-btn").style.display = "none"
      window.print()
      window.close()
    },
    handleClose() {
      window.close()
    }
  }
}
</script>

<style scoped>
/* ===== 屏幕预览 - 还原图片格式 ===== */
.print-pay-fee-container {
  width: 220mm;
  margin: 0 auto;
  padding: 3mm 5mm;
  background: #fff;
  /* 与打印一致：黑体（不加粗，仅关键信息加粗） */
  font-family: '黑体', SimHei, 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  color: #000;
}

/* 标题区：图标+小区名整体居中 */
.header-area {
  text-align: center;
  padding: 0.5mm 0 0 0;
}

.logo-icon {
  font-size: 22px;
  font-weight: bold;
  margin-right: 4px;
  vertical-align: middle;
}

.title-text {
  display: inline-block;
  font-size: 22px;
  font-weight: bold;
  margin: 0;
  letter-spacing: 3px;
  white-space: nowrap;
}

/* NO编号：标题下方，靠右 */
.receipt-info-bar {
  text-align: right;
  padding: 0.5mm 0 1mm 0;
}

.receipt-no {
  font-family: 'Segoe UI', 'Tahoma', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  font-feature-settings: "zero" 0, "tnum" 1;
}

/* 基本信息栏：日期 + 客户名称 + 结算方式同一行 */
.info-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 1mm 0;
  font-size: 14px;
}

.info-bar i {
  font-style: normal;
}

.customer-name-inline {
  flex: 1;
  font-size: 17px;
  font-weight: bold;
}

.pay-method-right {
  white-space: nowrap;
  font-size: 13px;
  flex-shrink: 0;
}

/* 明细表格 - 有边框 */
.receipt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.receipt-table th,
.receipt-table td {
  border: 1px solid #000;
  padding: 1mm 3px;
  vertical-align: middle;
  line-height: 1.3;
}

.receipt-table thead th {
  text-align: center;
  font-weight: bold;
  font-size: 14px;
}

.col-item { width: 58%; white-space: nowrap; }
.col-qty { width: 12%; }
.col-price { width: 12%; }
.col-money { width: 18%; font-weight: bold; }

.td-left { text-align: left; }
.td-center { text-align: center; }
.td-right { text-align: right; }

/* 数字专用字体：数字笔画分明、小数点对齐，针式/热敏打印更清晰
   不用 Consolas/Courier New——它们在 Windows 下数字0带斜杠(slash zero)，
   且 font-feature-settings 关不掉（字形内置）。改用 Segoe UI(无斜杠零) */
.num {
  font-family: 'Segoe UI', 'Tahoma', 'Microsoft YaHei', sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
  font-feature-settings: "zero" 0, "tnum" 1;
  font-variant-numeric: tabular-nums lining-nums;
}

.payer-label {
  font-weight: normal;
  font-family: 'Arial', 'Microsoft YaHei', '黑体', sans-serif;
  letter-spacing: 1.5px;
  margin-right: 6px;
}

.row-total td,
.row-remark td,
.row-footer td {
  font-size: 12.5px;
}

.total-label b,
.total-num b {
  font-weight: bold;
}

.remark-content {
  height: auto;
  min-height: 4mm;
  font-size: 12px;
}

/* 底部区域（表格外） */
.footer-outside {
  margin-top: 2mm;
  font-size: 12.5px;
  overflow: hidden;
}

.footer-note-left {
  float: left;
}

.footer-payee-right {
  float: right;
}

/* 操作按钮 */
.action-buttons {
  margin-top: 20px;
  text-align: center;
  padding: 10px 0;
}

/* ===== 打印样式 ===== */
@media print {
  @page {
    size: 241mm auto;
    margin: 5mm 5mm 5mm 0;
  }

  body { margin: 0; padding: 0; }

  .print-pay-fee-container {
    width: 100%;
    /* 打印机/驱动会在左边自带一片偏移（左边留白、右边贴边的根因），
       用负左外边距把整体往左推来补偿。数字越大越往左，按实际打印效果微调：
       左边还空 → 调更大（如 -20mm）；左边被切 → 调更小（如 -8mm） */
    margin: 0 0 0 -15mm;
    padding: 0;
    /* 针式/热敏打印机用黑体（不加粗，仅关键信息加粗） */
    font-family: '黑体', SimHei, 'Microsoft YaHei', sans-serif;
    font-size: 11pt;
  }

  .title-text { font-size: 17pt; }
  .receipt-info-bar { font-size: 10pt; }
  .receipt-no { font-size: 11pt; font-family: '黑体', SimHei, 'Microsoft YaHei', sans-serif; }
  .info-bar { font-size: 10.5pt; }
  .customer-name-inline { font-size: 13pt; font-weight: bold; }
  .pay-method-right { font-size: 9.5pt; }
  .receipt-table { font-size: 10.5pt; }
  .receipt-table thead th { font-size: 11pt; }
  .row-total td, .row-remark td { font-size: 10pt; }
  .footer-outside { font-size: 9.5pt; }
  .remark-content { font-size: 9.5pt; }
  /* 打印时数字字体强化：加粗 + 等宽数字(tnum 对齐)，确保针式/热敏打印数字清晰可辨
     不用 Consolas/Courier New(0带斜杠)，用 Segoe UI(0无斜杠) */
  .num {
    font-family: 'Segoe UI', 'Tahoma', 'Microsoft YaHei', sans-serif;
    font-weight: 700;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-feature-settings: "zero" 0, "tnum" 1;
  }
  .total-num .num { font-size: 12pt; }
  .payer-label {
    font-weight: normal;
    font-family: 'Arial', 'Microsoft YaHei', '黑体', sans-serif;
    letter-spacing: 2px;
    font-size: 11pt;
  }

  #print-btn { display: none !important; }
  .action-buttons { display: none !important; }
}
</style>

<!-- 全局（非 scoped）：打印时清除外层 body/#app 的默认边距，避免内容右侧偏移、左侧留白 -->
<style>
@media print {
  html,
  body,
  #app,
  #app > div {
    margin: 0 !important;
    padding: 0 !important;
  }
}
</style>